import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router";
import { ArrowRight, Building2, Flower2, MapPin, UserRound } from "lucide-react";

import { getSupabaseClient } from "../providers/supabase/supabase";
import type { Deal } from "../types";

// LDF: Beauftragungsweg eines Auftrags als Kette Endkunde → (Agentur / Location) → Lovely Day Flowers.
// Jede Station zeigt ihren eigenen Kontaktkreis.

export const ORDER_CHANNELS = [
  { value: "direkt", label: "Direkt vom Endkunden" },
  { value: "location", label: "Über die Location" },
  { value: "agentur", label: "Über eine Eventagentur" },
];

export const orderChannelLabel = (value?: string | null) => ORDER_CHANNELS.find((c) => c.value === value)?.label;

type Station = { id: number; name: string; contacts: { id: number; name: string; title: string | null }[] };

const loadStations = async (ids: number[], dealContactIds: number[]) => {
  if (!ids.length) return new Map<number, Station>();
  const supabase = getSupabaseClient();
  const [companies, contacts] = await Promise.all([
    supabase.from("companies").select("id,name").in("id", ids),
    supabase.from("contacts").select("id,first_name,last_name,title,company_id").in("company_id", ids).order("last_seen", { ascending: false }),
  ]);
  const map = new Map<number, Station>();
  for (const c of companies.data ?? []) map.set(c.id, { id: c.id, name: c.name, contacts: [] });
  for (const p of contacts.data ?? []) {
    const station = map.get(p.company_id);
    if (!station) continue;
    const entry = { id: p.id, name: [p.first_name, p.last_name].filter(Boolean).join(" "), title: p.title };
    // Kontakte des Auftrags zuerst
    if (dealContactIds.includes(p.id)) station.contacts.unshift(entry);
    else station.contacts.push(entry);
  }
  return map;
};

const StationBox = ({
  role,
  station,
  icon: Icon,
  highlight,
  dealContactIds,
  to,
}: {
  role: string;
  station?: Station;
  icon: typeof Building2;
  highlight?: boolean;
  dealContactIds: number[];
  to?: string;
}) => (
  <div className={`min-w-0 flex-1 rounded-lg border p-3 ${highlight ? "border-primary/40 bg-primary/5" : "border-border"}`}>
    <span className="flex items-center gap-1 text-xs text-muted-foreground tracking-wide">
      <Icon className="h-3.5 w-3.5" /> {role}
    </span>
    {station ? (
      <>
        <Link to={to ?? `/companies/${station.id}/show`} className="block truncate text-sm font-medium underline hover:no-underline">
          {station.name}
        </Link>
        <ul className="mt-1 space-y-0.5">
          {station.contacts.slice(0, 4).map((c) => (
            <li key={c.id} className="flex items-center gap-1 text-xs">
              <UserRound className="h-3 w-3 shrink-0 text-muted-foreground" />
              <Link to={`/contacts/${c.id}/show`} className={`truncate ${dealContactIds.includes(c.id) ? "font-medium" : "text-muted-foreground"}`}>
                {c.name}
              </Link>
              {c.title && <span className="truncate text-muted-foreground">· {c.title}</span>}
            </li>
          ))}
          {station.contacts.length > 4 && <li className="text-xs text-muted-foreground">+ {station.contacts.length - 4} weitere</li>}
          {!station.contacts.length && <li className="text-xs text-muted-foreground">Noch keine Kontakte</li>}
        </ul>
      </>
    ) : (
      <span className="block text-sm text-muted-foreground">nicht eingetragen</span>
    )}
  </div>
);

const Arrow = () => <ArrowRight className="hidden h-4 w-4 shrink-0 self-center text-muted-foreground sm:block" />;

export const DealChannel = ({ record }: { record: Deal }) => {
  const clientId = Number(record.company_id);
  const endClientId = record.end_client_company_id ? Number(record.end_client_company_id) : null;
  const locationId = record.location_company_id ? Number(record.location_company_id) : null;
  const dealContactIds = (record.contact_ids ?? []).map(Number);
  const ids = [...new Set([clientId, endClientId, locationId].filter((x): x is number => !!x))];

  const { data: stations } = useQuery({
    queryKey: ["deal-channel", ids, dealContactIds],
    queryFn: () => loadStations(ids, dealContactIds),
  });

  const channel = record.order_channel ?? "direkt";
  const get = (id: number | null) => (id ? stations?.get(id) : undefined);
  const direct = channel === "direkt" || endClientId === clientId;

  return (
    <div className="m-4">
      <span className="text-xs text-muted-foreground tracking-wide">
        Beauftragungsweg: <span className="font-medium text-foreground">{orderChannelLabel(channel)}</span>
      </span>
      <div className="mt-2 flex flex-col gap-2 sm:flex-row">
        <StationBox role={direct ? "Endkunde & Auftraggeber" : "Endkunde"} station={get(direct ? clientId : endClientId)} icon={Building2} highlight={direct} dealContactIds={dealContactIds} />
        {!direct && (
          <>
            <Arrow />
            <StationBox
              role={channel === "location" ? "Auftraggeber · Location-Betreiber" : "Auftraggeber · Eventagentur"}
              station={get(clientId)}
              icon={Building2}
              highlight
              dealContactIds={dealContactIds}
            />
          </>
        )}
        <Arrow />
        <div className="flex items-center gap-2 rounded-lg border border-dashed border-border px-3 py-2 text-sm sm:w-40">
          <Flower2 className="h-4 w-4 text-muted-foreground" /> Lovely Day Flowers
        </div>
      </div>
      {locationId && (
        <div className="mt-2 sm:w-1/2">
          <StationBox role="Veranstaltungsort" station={get(locationId)} icon={MapPin} dealContactIds={dealContactIds} to={`/companies/${locationId}/show/location`} />
        </div>
      )}
      <p className="mt-1 text-xs text-muted-foreground">
        Angebot, Preis und Rechnung mit dem Auftraggeber · Wünsche und Gestaltung mit dem Endkunden · Aufbau und Zeitfenster mit der Location
      </p>
    </div>
  );
};
